import axios from 'axios';
import {logout} from "./AuthService";
import Routing from "./routing";

let refreshPromise = null;

// Renouvelle le token via /api/token/refresh. Un seul appel en vol à la fois :
// les requêtes concurrentes partagent la même promesse pour ne pas consommer
// prématurément le refresh_token (usage unique, rotation à chaque appel).
function refreshToken() {
    if (!refreshPromise) {
        refreshPromise = performRefresh().finally(() => {
            refreshPromise = null;
        });
    }
    return refreshPromise;
}

async function performRefresh() {
    const storedRefreshToken = localStorage.getItem('refresh_token');
    if (!storedRefreshToken) {
        throw new Error('Aucun refresh token disponible');
    }

    const response = await axios.post(
        `${process.env.REACT_APP_API_URL}${Routing.routesApi.tokenRefresh}`,
        {refresh_token: storedRefreshToken}
    );

    const {token, refresh_token} = response.data;
    localStorage.setItem('jwt_token', token);
    localStorage.setItem('refresh_token', refresh_token);
    return token;
}

const ERROR_HANDLERS = {
    400: (error) => {
        const message = error.response.data?.message || "Requête invalide.";
        console.warn("[400 Bad Request]", message);
        // ex: afficher une notification toast
    },
    401: async (error) => {
        const originalRequest = error.config;
        if (originalRequest._retry) {
            console.warn("[401 Unauthorized] Échec du renouvellement du token, déconnexion.");
            logout();
            return null;
        }
        originalRequest._retry = true;

        try {
            const newToken = await refreshToken();
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            return AxiosConfig(originalRequest);
        } catch (refreshError) {
            console.warn("[401 Unauthorized] Échec du renouvellement du token, déconnexion.");
            logout();
            return null;
        }
    },
    403: (error) => {
        console.warn("[403 Forbidden] Accès refusé.");
        // window.location.href = "/unauthorized";
    },
    404: (error) => {
        const url = `${error.config.baseURL}${error.config.url}`;
        console.warn(`[404 Not Found] Ressource introuvable : ${url}`);
        // window.location.href = "/404";
    },
    422: (error) => {
        const errors = error.response.data?.errors;
        console.warn("[422 Unprocessable Entity] Erreurs de validation :", errors);
        // ex: passer les erreurs à un form handler
    },
    429: (error) => {
        console.warn("[429 Too Many Requests] Limite atteinte, réessayez plus tard.");
    },
    500: (error) => {
        console.error("[500 Internal Server Error] Erreur serveur.");
        // ex: rediriger vers une page /500
    },
    503: (error) => {
        console.error("[503 Service Unavailable] Serveur indisponible.");
    },
};

const AxiosConfig = axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    headers: {
        'Content-Type': 'application/json'
    }
});

AxiosConfig.interceptors.request.use(async (config) => {
    const token = localStorage.getItem('jwt_token');
    if (token) {
        if (isJwtExpired(token)) {
            try {
                const newToken = await refreshToken();
                config.headers['Authorization'] = `Bearer ${newToken}`;
            } catch (refreshError) {
                logout();
                throw new axios.Cancel('Token expiré - déconnecté');
            }
        } else {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
    }

    if (process.env.REACT_APP_MODE === 'dev') {
        console.log(`[Request ${config.method}] ${config.baseURL}${config.url}`);
    }

    return config;
}, (error) => {
    console.error("[Request Error]", error);
    return Promise.reject(error);
});

// Intercepteur de réponse pour gérer les erreurs de réseau
AxiosConfig.interceptors.response.use(
    (response) => {
        if (process.env.REACT_APP_MODE === 'dev') {
            console.log(`[Response ${response.status}] ${response.config.baseURL}${response.config.url}`, response.data);
        }
        return response;
    },
    async (error) => {
        if (!error.response) {
            console.error("Erreur réseau : Impossible de se connecter au serveur API.");
            console.log("[Network Error]", error);
            alert("Erreur réseau : Impossible de se connecter au serveur.");
        } else {
            const status = error.response.status;
            const url = `${error.config.baseURL}${error.config.url}`;
            console.error(`[Response Error] ${url}, Status: ${status}`, error.response.data);

            const handler = ERROR_HANDLERS[status] ?? handleUnknownError;
            const result = await handler(error);
            if (result) {
                return result;
            }
        }
        return Promise.reject(error);
    }
);

function handleUnknownError(error) {
    console.error("[Unknown Error]", error.response ? error.response.data : error);
}

function isJwtExpired(token) {
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.exp * 1000 < Date.now();
    } catch {
        return true;
    }
}

export default AxiosConfig;
